import React, { useState, useEffect } from "react";
import {
  Table,
  Tooltip,
  OverlayTrigger
} from "react-bootstrap";
import Pagination from "@mui/material/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import styles from "page.module.css";
import FlipPanel from "components/FlipPanel/FlipPanel";

const KeywordIdeasTable = ({ data, itemsPerPage = 25 }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(null);
  const [isFlipPanelOpen, setIsFlipPanelOpen] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  
  // Reset to first page when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);
  
  // Safety check for undefined or null data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-muted">No data available to display.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, data.length);

  // Filter data based on search query and sort by "Avg. Monthly Searches"
  const filteredData = searchQuery
    ? data.filter((item) =>
      item.keyword.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : data;

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortOrder === "asc") {
      return a.avg_monthly_searches - b.avg_monthly_searches;
    } else if (sortOrder === "desc") {
      return b.avg_monthly_searches - a.avg_monthly_searches;
    }
    return 0;
  });

  const currentItems = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (event, value) => setCurrentPage(value);

  const handleSortOrderChange = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleAdvanceClick = (item) => {
    setSelectedKeyword(item);
    setIsFlipPanelOpen(true);
  };

  const closeFlipPanel = () => {
    setIsFlipPanelOpen(false);
    setSelectedKeyword(null);
  };

  return (
    <>
      {/* Flip Panel Component */}
      <FlipPanel
        isOpen={isFlipPanelOpen}
        onClose={closeFlipPanel}
        selectedKeyword={selectedKeyword}
        sourceType={"keyword"}
      />
      
      <div>
        <p
          className="d-flex fw-normal justify-content-start align-items-center"
          style={{ color: "#666666" }}
        >
          Showing {startIndex + 1} - {endIndex} of {data.length} keywords
        </p>
      </div>
      <div className={styles.table_container}>
        <div className="table-responsive mt-5">
          <Table hover className="text-wrap text-break">
            <thead>
              <tr>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{
                    backgroundColor: "white",
                    borderRight: "2px solid white",
                  }}
                >
                  NOS
                </th>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{
                    backgroundColor: "white",
                    borderRight: "2px solid white",
                  }}
                >
                  Keywords
                </th>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{
                    backgroundColor: "white",
                    borderRight: "2px solid white",
                  }}
                >
                  Avg. Monthly Searches
                  <span
                    onClick={handleSortOrderChange}
                    style={{ cursor: "pointer", marginLeft: "8px" }}
                  >
                    <FontAwesomeIcon
                      icon={sortOrder === "asc" ? faArrowUp : faArrowDown}
                      className="ms-1"
                    />
                  </span>
                </th>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{ backgroundColor: "white" }}
                >
                  YoY Change
                </th>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{ backgroundColor: "white" }}
                >
                  3-month change
                </th>
                <th
                  className="text-center p-1 text-nowrap"
                  style={{ backgroundColor: "white" }}
                >
                  Tasks
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => (
                <tr key={index} className="hover-row">
                  <td className="text-center align-middle">
                    {startIndex + index + 1}
                  </td>
                  <td className="text-nowrap">{item.keyword}</td>
                  <td className="text-center align-middle text-nowrap">
                    {item.avg_monthly_searches}
                  </td>
                  <td className="text-center align-middle text-nowrap">
                    {Math.round(item.yoy_change * 100) / 100}%
                  </td>
                  <td className="text-center align-middle text-nowrap">
                    {Math.round(item.three_month_change * 100) / 100}%
                  </td>
                  <td className="text-center align-middle text-nowrap">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAdvanceClick(item)}
                    >
                      Advance
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="d-flex justify-content-center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="primary"
          />
        </div>
      </div>
    </>
  );
};

export default KeywordIdeasTable;
